import {
  ComboboxSingle,
  type TComboboxSingleRootProps,
} from '@crehana/compass.ui/v3/combobox';
import { DatePicker } from '@crehana/compass.ui/v3/date-picker';
import { Input, InputLabel, InputMessage } from '@crehana/compass.ui/v3/input';
import { BasicTooltip } from '@crehana/compass.ui/v3/tooltip';
import { useId } from 'react';

import { useTranslation } from '../../../i18n';
import type { TAttendanceCardDayShift } from '../../../types';

type Props = {
  date: Date | undefined;
  onDateChange: (date: Date | undefined) => void;
  shifts: Array<TAttendanceCardDayShift> | undefined;
  shiftLoading: boolean;
  shiftError: boolean;
  selectedShiftId: string | null;
  onShiftIdChange: (shiftId: string | null) => void;
  showDateError: boolean;
  showShiftError: boolean;
};

export const RequestDateShiftFields = ({
  date,
  onDateChange,
  shifts,
  shiftLoading,
  shiftError,
  selectedShiftId,
  onShiftIdChange,
  showDateError,
  showShiftError,
}: Props) => {
  const { t, languageCode } = useTranslation();
  const shiftMessageId = useId();
  const shiftDescriptionId = useId();
  const shiftInputId = useId();

  // TIM-663 CAMBIO A (Julian, 2026-09-22) — `shifts` now comes from a real
  // list per day, so the selector for 2+ turnos can come back: the backend
  // can finally tell them apart by id. 0 turnos keeps blocking Guardar; 1
  // turno stays a plain read-only value; 2+ turnos requires a pick.
  const shiftsResolved =
    Boolean(date) && !shiftLoading && !shiftError && shifts !== undefined;
  const shiftCount = shiftsResolved ? (shifts?.length ?? 0) : 0;
  const hasNoShift = shiftsResolved && shiftCount === 0;
  const hasOneShift = shiftsResolved && shiftCount === 1;
  const needsSelection = shiftsResolved && shiftCount >= 2;

  const shiftItems: TComboboxSingleRootProps<string>['items'] =
    shifts?.map(shift => shift.id) ?? [];
  const selectedShiftLabel = selectedShiftId
    ? (shifts?.find(shift => shift.id === selectedShiftId)?.name ??
      selectedShiftId)
    : null;
  const readOnlyValue = hasOneShift ? (shifts?.[0]?.name ?? '') : '';

  // TIM-560 (Julian, 2026-09-23) — Product moved the 0/1-turno messaging off
  // the field body and onto a disabled input + tooltip: Guardar already
  // blocks on `errors.shift` for the 0-turno case, so a below-field message
  // duplicated what the disabled Save button says. The 2+ combobox keeps its
  // own touched-gated InputMessage below — that branch is rule 4, untouched.
  const shiftDisabledTooltip = hasNoShift
    ? t('CREATE_DIALOG_SHIFT_NONE_TOOLTIP')
    : t('CREATE_DIALOG_SHIFT_ONE_TOOLTIP');

  return (
    <>
      <div className="tw-flex tw-flex-col tw-cds-gap-2">
        <InputLabel required>{t('CREATE_DIALOG_DATE_LABEL')}</InputLabel>
        <DatePicker
          mode="single"
          language={languageCode}
          placeholder={t('CREATE_DIALOG_DATE_PLACEHOLDER')}
          selected={date}
          onSelect={onDateChange}
          error={showDateError}
        />
        {showDateError && (
          <InputMessage error>{t('VALIDATION_DATE_REQUIRED')}</InputMessage>
        )}
      </div>

      <div className="tw-flex tw-flex-col tw-cds-gap-2">
        <InputLabel htmlFor={shiftInputId} required={needsSelection}>
          {t('CREATE_DIALOG_SHIFT_LABEL')}
        </InputLabel>
        {(hasNoShift || hasOneShift) && (
          // B9 pattern (edit-workday-overtime-time-field.tsx) — a `disabled`
          // native input emits no pointer events, so BasicTooltip must wrap a
          // separate, focusable, non-disabled `<span>`, never the input
          // itself. The sr-only sibling carries the same text via
          // aria-describedby so a screen reader gets it without hovering —
          // the tooltip alone is not enough (TIM-560 review).
          <BasicTooltip content={shiftDisabledTooltip}>
            <span tabIndex={0} className="tw-block">
              {/* A disabled input emits no pointer events AND covers the
                  middle of the wrapping span, so the hover never reaches the
                  tooltip trigger. Measured: the span is 712x40 and the input
                  678x18 inside it, and elementFromPoint at the span centre
                  returns the disabled input. `pointer-events: none` lets the
                  hover fall through to the span. */}
              <Input
                id={shiftInputId}
                disabled
                className="tw-pointer-events-none"
                value={readOnlyValue}
                placeholder={
                  hasNoShift
                    ? t('CREATE_DIALOG_SHIFT_NONE_PLACEHOLDER')
                    : undefined
                }
                aria-describedby={shiftDescriptionId}
              />
              <span id={shiftDescriptionId} className="tw-sr-only">
                {shiftDisabledTooltip}
              </span>
            </span>
          </BasicTooltip>
        )}
        {!needsSelection && !hasNoShift && !hasOneShift && (
          <Input id={shiftInputId} readOnly value="" />
        )}
        {needsSelection && (
          <ComboboxSingle.Root
            items={shiftItems}
            value={selectedShiftId}
            onValueChange={onShiftIdChange}
          >
            <ComboboxSingle.TriggerValue
              id={shiftInputId}
              placeholder={t('CREATE_DIALOG_SHIFT_PLACEHOLDER')}
              aria-describedby={showShiftError ? shiftMessageId : undefined}
            >
              {selectedShiftLabel}
            </ComboboxSingle.TriggerValue>
            <ComboboxSingle.Content>
              <ComboboxSingle.List>
                {shifts?.map(shift => (
                  <ComboboxSingle.Item
                    key={shift.id}
                    value={shift.id}
                    label={shift.name}
                  >
                    {shift.name}
                  </ComboboxSingle.Item>
                ))}
              </ComboboxSingle.List>
            </ComboboxSingle.Content>
          </ComboboxSingle.Root>
        )}
        {needsSelection && showShiftError && (
          <InputMessage id={shiftMessageId} error>
            {t('VALIDATION_SHIFT_REQUIRED')}
          </InputMessage>
        )}
      </div>
    </>
  );
};
